<script lang="ts">
import type { Map } from "maplibre-gl";
import { Debounced, onClickOutside } from "runed";
import type { LngLat } from "../../lib/geo/types.ts";
import {
  POI_CATEGORIES,
  type PoiCategory,
} from "../../lib/search/categories.ts";
import type { PoiSearchState } from "../../lib/search/controller.ts";
import { SEARCH_ICONS } from "../../lib/search/searchIcons.ts";
import * as Command from "../ui/command/index.ts";
import { Spinner } from "../ui/spinner/index.ts";
import type { OsmSelectionTarget } from "../MapScreen/selection/osmLookup.ts";
import SearchInput from "./SearchInput.svelte";
import {
  fetchPhotonFeatures,
  photonMainClassification,
  type PhotonFeature,
} from "./photon.ts";
import {
  formatSearchResultLabel,
  iconForPhotonClassification,
  photonFeatureSelectionTarget,
} from "./photonFeature.ts";
import {
  buildSearchSuggestionsModel,
  type GeocoderSearchState,
} from "./suggestions.ts";
import FeaturedCategories from "./FeaturedCategories.svelte";

let {
  map,
  photonApiUrl,
  userLngLat,
  ongeocoderselect,
  oncategoryselect,
  onclear,
  poiSearch,
}: {
  map: Map;
  photonApiUrl: string;
  userLngLat: LngLat | undefined;
  ongeocoderselect: (selectionTarget: OsmSelectionTarget) => void;
  oncategoryselect: (category: PoiCategory) => void;
  onclear: () => void;
  poiSearch: PoiSearchState;
} = $props();

let inputValue = $state("");
let geocoderState = $state<GeocoderSearchState>({ status: "idle" });
let searchRoot = $state<HTMLDivElement>()!;
let suggestionsVisible = $state(false);
let showAllCategories = $state(false);
let requestId = 0;
const loggedUnmappedClassificationPairs = new Set<string>();

onClickOutside(
  () => searchRoot,
  () => {
    suggestionsVisible = false;
    showAllCategories = false;
  },
);

const debouncedInput = new Debounced(() => inputValue, 300);
const suggestions = $derived(buildSearchSuggestionsModel(inputValue, geocoderState));
const featuredCategories = $derived(
  showAllCategories ? POI_CATEGORIES : suggestions.categories,
);

// $effect (not $derived) because search() is async and mutates search state.
// Reads debouncedInput.current so Svelte tracks it as a dependency and re-runs
// when the debounced value settles. Also runs on mount, but the initial value is ""
// so it hits the early return harmlessly.
$effect(() => {
  if (!suggestionsVisible) return;
  const query = debouncedInput.current;
  if (query.trim() === "") {
    geocoderState = { status: "idle" };
    return;
  }
  search(query);
});

async function search(query: string): Promise<void> {
  const currentRequestId = ++requestId;
  geocoderState = { status: "loading", query };

  try {
    const center = map.getCenter();
    const results = await fetchPhotonFeatures(
      photonApiUrl,
      query,
      [center.lng, center.lat],
    );
    // Guard against stale responses arriving out of order
    if (currentRequestId !== requestId || query !== inputValue) return;
    logUnmappedPhotonClassifications(results);
    geocoderState = {
      status: "success",
      query,
      results,
    };
  } catch (err) {
    console.error("Geocoding request failed:", err);
    if (currentRequestId !== requestId || query !== inputValue) return;
    geocoderState = { status: "error", query };
  }
}

function logUnmappedPhotonClassifications(results: PhotonFeature[]): void {
  for (const feature of results) {
    const classification = photonMainClassification(feature);
    if (iconForPhotonClassification(classification) !== "Search") {
      continue;
    }

    const pair = `${classification.key}=${classification.value}`;
    if (loggedUnmappedClassificationPairs.has(pair)) continue;

    loggedUnmappedClassificationPairs.add(pair);
    console.info("Photon classification has no icon mapping; using fallback.", {
      osm_key: classification.key,
      osm_value: classification.value,
    });
  }
}

function selectGeocoderResult(feature: PhotonFeature): void {
  const selectionTarget = photonFeatureSelectionTarget(feature);
  suggestionsVisible = false;
  ongeocoderselect(selectionTarget);
}

function selectCategory(category: PoiCategory): void {
  inputValue = category.label;
  suggestionsVisible = false;
  oncategoryselect(category);
}

function clearSearch(): void {
  geocoderState = { status: "idle" };
  showAllCategories = false;
  onclear();
}
</script>

{#snippet categorySuggestion(category: PoiCategory)}
  <Command.Item
    value={`category-${category.id}`}
    onSelect={() => selectCategory(category)}
  >
    {@const CategoryIcon = SEARCH_ICONS[category.icon]}
    <div class="flex items-center gap-2">
      <span
        class="flex size-8 shrink-0 items-center justify-center"
        aria-hidden="true"
      >
        <CategoryIcon class="size-4" />
      </span>
      <span class="font-medium">{category.label}</span>
    </div>
  </Command.Item>
{/snippet}

{#snippet suggestionMessage(message: string, className = "text-muted-foreground")}
  <p class={`py-6 text-center text-sm ${className}`}>
    {message}
  </p>
{/snippet}

<!-- Search suggestions should draw over SelectionPanel, which uses z-[4]. -->
<div
  class="relative z-[5] w-96"
  bind:this={searchRoot}
  onfocusin={() => suggestionsVisible = true}
>
  <Command.Root
    shouldFilter={false}
    label="Search places"
    class="max-h-[500px] rounded-md border shadow-md"
  >
    <SearchInput
      placeholder="Search places…"
      bind:value={inputValue}
      onclear={clearSearch}
    />
    {#if poiSearch.status === "loading" && poiSearch.source === "search"}
      <div
        class="flex items-center justify-center gap-2 border-t py-3 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        <Spinner class="size-4" role="presentation" aria-hidden="true" />
        Searching {poiSearch.category.label}…
      </div>
    {/if}
    {#if suggestionsVisible}
      <div class="min-h-0 overflow-y-auto border-t">
        {#if suggestions.state === "featured"}
          <Command.List class="max-h-none overflow-visible">
            <FeaturedCategories
              categories={featuredCategories}
              expanded={showAllCategories}
              oncategoryselect={selectCategory}
              ontoggleexpanded={() => showAllCategories = !showAllCategories}
            />
          </Command.List>
        {:else}
          {@const hasCategories = suggestions.categories.length > 0}
          {@const hasPlaces = suggestions.geocoderResults.length > 0}
          {@const hasItems = hasCategories || hasPlaces}

          {#if hasItems}
            <Command.List class="max-h-none overflow-visible">
              {#if hasCategories}
                <Command.Group heading="Categories">
                  {#each suggestions.categories as category}
                    {@render categorySuggestion(category)}
                  {/each}
                </Command.Group>
              {/if}
              {#if hasCategories && hasPlaces}
                <Command.Separator />
              {/if}
              {#if hasPlaces}
                <Command.Group heading="Places">
                  {#each suggestions.geocoderResults as feature, i (i)}
                    {@const { title, subtitle } = formatSearchResultLabel(
                      feature,
                      userLngLat,
                    )}
                    {@const ResultIcon = SEARCH_ICONS[
                      iconForPhotonClassification(photonMainClassification(feature))
                    ]}
                    <Command.Item
                      value={`geocoder-${i}`}
                      onSelect={() => selectGeocoderResult(feature)}
                    >
                      <div class="flex items-center gap-2">
                        <span
                          class="flex size-8 shrink-0 items-center justify-center"
                          aria-hidden="true"
                        >
                          <ResultIcon class="size-4" />
                        </span>
                        <div class="flex flex-col">
                          <span class="font-medium">{title}</span>
                          {#if subtitle}
                            <span class="text-xs text-muted-foreground">
                              {subtitle}
                            </span>
                          {/if}
                        </div>
                      </div>
                    </Command.Item>
                  {/each}
                </Command.Group>
              {/if}
            </Command.List>
          {/if}

          {#if suggestions.geocoderStatus === "loading"}
            <div
              class="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              <Spinner class="size-4" role="presentation" aria-hidden="true" />
              Searching places…
            </div>
          {:else if suggestions.geocoderStatus === "error"}
            {@render suggestionMessage(
              "Place search failed. Please try again.",
              "text-destructive",
            )}
          {:else if suggestions.noSuggestionsForQuery}
            <Command.Empty>No results found.</Command.Empty>
          {/if}
        {/if}
      </div>
    {/if}
  </Command.Root>
</div>

<style>
/* Search lives inside MapLibre's top-left control container, so the whole
   container must stack above SelectionPanel for suggestions to overlay it. */
:global(.maplibregl-ctrl-top-left) {
  z-index: 5;
}
</style>
